/**
 * Slideshow Swiper Component
 * 幻灯片轮播组件 - 使用 Swiper 库
 * 支持多个实例，自动初始化页面上的所有 slideshow-swiper 组件
 */

(function() {
  'use strict';

  // 等待 DOM 和 Swiper 库加载完成
  function initializeSlideshows() {
    // 检查 Swiper 是否已加载
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper library not loaded yet, retrying...');
      setTimeout(initializeSlideshows, 100);
      return;
    }

    // 查找所有未初始化的 slideshow-swiper 实例
    const slideshowSections = document.querySelectorAll('.shopify-section--slideshow-swiper:not([data-swiper-initialized])');

    slideshowSections.forEach(section => {
      const swiperEl = section.querySelector('.swiper');
      if (!swiperEl) return;

      // 标记为已初始化
      section.setAttribute('data-swiper-initialized', 'true');

      // 初始化该实例
      initializeSlideshowSwiper(section, swiperEl);
    });
  }

  /**
   * 初始化单个 Slideshow Swiper 实例
   */
  function initializeSlideshowSwiper(section, swiperEl) {
    const swiperContainer = section.querySelector('.slideshow-swiper');

    // 标记是否为首次加载
    let isFirstLoad = true;
    let hasLeftFirstSlide = false;

    // 从 DOM 读取配置
    const config = getSwiperConfig(swiperEl);

    // 初始化 Swiper
    const swiper = new Swiper(swiperEl, {
      ...config,

      // 键盘控制
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // 触摸滑动
      touchRatio: 1,
      grabCursor: true,

      // 监听切换事件
      on: {
        slideChange: function() {
          // 如果是首次加载且切换到了非第一屏，标记已离开首屏
          if (isFirstLoad && this.realIndex !== 0) {
            hasLeftFirstSlide = true;
          }

          // 一旦离开过第一屏，移除 first-load 类，允许后续动画
          if (hasLeftFirstSlide && swiperContainer) {
            swiperContainer.classList.remove('first-load');
            isFirstLoad = false;
          }

          // 更新自定义分页器状态
          updateCustomPagination(section, this.realIndex);

          // 更新控制器颜色和背景
          updateControlsColor(section, swiperContainer, this.slides[this.activeIndex]);
        },
        init: function() {
          // 清除首屏 LCP 内联样式；loop 会在 DOM 首位插入最后一屏克隆，内联 opacity 会干扰切换
          const initialSlide = swiperEl.querySelector('.slideshow-swiper__initial-slide');
          if (initialSlide) {
            initialSlide.style.removeProperty('opacity');
            initialSlide.style.removeProperty('visibility');
          }

          // 初始化自定义分页器
          updateCustomPagination(section, 0);

          // 初始化控制器颜色
          const firstSlide = this.slides[0];
          updateControlsColor(section, swiperContainer, firstSlide);
        }
      }
    });

    // 绑定自定义分页器事件
    bindPaginationEvents(section, swiper, swiperContainer, () => {
      if (isFirstLoad) {
        hasLeftFirstSlide = true;
        if (swiperContainer) {
          swiperContainer.classList.remove('first-load');
        }
        isFirstLoad = false;
      }
    });

    // 绑定视频弹窗事件
    bindVideoModalEvents(section);
  }

  /**
   * 从 DOM 读取 Swiper 配置
   */
  function getSwiperConfig(swiperEl) {
    const config = {};

    // 从 data 属性读取配置
    const transitionType = swiperEl.dataset.transitionType;
    const autoplay = swiperEl.dataset.autoplay === 'true';
    const cycleSpeed = parseInt(swiperEl.dataset.cycleSpeed || '6', 10);
    const loop = swiperEl.dataset.loop === 'true';
    const showProgressBar = swiperEl.dataset.showProgressBar === 'true';
    const sideArrowsEnabled = swiperEl.dataset.sideArrowsEnabled === 'true';
    const controlsType = swiperEl.dataset.controlsType;

    // 转场效果
    if (transitionType === 'fade' || transitionType === 'fade_with_text') {
      config.effect = 'fade';
      config.fadeEffect = {
        crossFade: true
      };
    }

    // 自动播放
    if (autoplay) {
      config.autoplay = {
        delay: cycleSpeed * 1000,
        disableOnInteraction: false,
      };
    }

    // 循环
    config.loop = loop;
    config.speed = 600;

    // 进度条
    if (showProgressBar) {
      config.pagination = {
        el: '.swiper-pagination-progressbar',
        type: 'progressbar',
      };
    }

    // 导航箭头
    if (sideArrowsEnabled) {
      config.navigation = {
        nextEl: '.swiper-button-next-custom',
        prevEl: '.swiper-button-prev-custom',
      };
    } else if (controlsType === 'filled_arrows' || controlsType === 'outline_arrows') {
      config.navigation = {
        nextEl: '.swiper-button-next-custom-bottom',
        prevEl: '.swiper-button-prev-custom-bottom',
      };
    }

    return config;
  }

  /**
   * 更新自定义分页器状态
   */
  function updateCustomPagination(section, activeIndex) {
    const dotsButtons = section.querySelectorAll('.stretching-dots button, .numbered-dots button');
    dotsButtons.forEach((btn, index) => {
      btn.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }

  /**
   * 更新控制器颜色
   */
  function updateControlsColor(section, swiperContainer, activeSlide) {
    if (!activeSlide) return;

    const slideStyles = getComputedStyle(activeSlide);
    const controlsBackground = slideStyles.getPropertyValue('--slideshow-slide-controls-background');
    const controlsColor = slideStyles.getPropertyValue('--slideshow-slide-controls-color');

    if (controlsBackground) {
      section.style.setProperty('--slideshow-controls-background', controlsBackground);
    }
    if (controlsColor) {
      section.style.setProperty('--slideshow-controls-color', controlsColor);
    }

    // 如果是 boxed 模式，更新背景
    if (swiperContainer && swiperContainer.classList.contains('slideshow-swiper--boxed')) {
      const backgroundElement = section.querySelector('.slideshow-swiper__slide-background');
      const background = slideStyles.getPropertyValue('--slideshow-slide-background');

      if (backgroundElement && background) {
        // 设置新背景并淡入
        backgroundElement.style.background = background;
        backgroundElement.animate(
          { opacity: [0, 1] },
          { duration: 200, fill: 'forwards' }
        ).finished.then(() => {
          // 更新容器背景
          swiperContainer.style.setProperty('--slideshow-background', background);
          // 淡出过渡元素
          backgroundElement.animate(
            { opacity: 0 },
            { duration: 0, fill: 'forwards' }
          );
        });
      }
    }
  }

  /**
   * 绑定自定义分页器点击事件
   */
  function bindPaginationEvents(section, swiper, swiperContainer, onFirstSlideLeave) {
    const paginationButtons = section.querySelectorAll('.swiper-pagination-custom button');
    paginationButtons.forEach((btn) => {
      btn.addEventListener('click', function() {
        const slideIndex = parseInt(this.getAttribute('data-slide-index'), 10);

        // 如果点击了非第一屏，触发回调
        if (slideIndex !== 0) {
          onFirstSlideLeave();
        }

        swiper.slideToLoop(slideIndex);
      });
    });
  }

  /**
   * 绑定视频弹窗事件
   */
  function bindVideoModalEvents(section) {
    const videoModals = section.querySelectorAll('.video-modal');

    function stopMedia(mediaElement) {
      if (mediaElement.tagName === 'VIDEO') {
        mediaElement.pause();
        mediaElement.currentTime = 0;
      } else if (mediaElement.tagName === 'IFRAME') {
        if (mediaElement.hasAttribute('src')) {
          mediaElement.setAttribute('data-src', mediaElement.getAttribute('src'));
          mediaElement.setAttribute('src', 'about:blank');
        }
      }
    }

    function closeModal(modal) {
      if (!modal) return;
      modal.classList.remove('active');
      const modalContent = modal.querySelector('.video-modal__content');
      if (modalContent) {
        [...modalContent.querySelectorAll('video, iframe')].forEach(stopMedia);
      }
      document.body.classList.remove('modal-open');
    }

    function openModal(modalId) {
      const targetModal = document.getElementById(modalId);
      if (!targetModal) return;
      targetModal.classList.add('active');
      document.body.classList.add('modal-open');
    }

    // 初始化弹窗事件
    videoModals.forEach((modal) => {
      const closeButton = modal.querySelector('.video-modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => closeModal(modal));
      }
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal(modal);
      });
    });

    // ESC 键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const activeModal = section.querySelector('.video-modal.active');
        if (activeModal) closeModal(activeModal);
      }
    });

    // 视频触发器
    const videoTriggers = section.querySelectorAll('[data-video-trigger="true"]');
    videoTriggers.forEach((trigger) => {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const modalId = this.getAttribute('data-target');
        if (modalId) openModal(modalId);
      });
    });
  }

  // 在 DOM 加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSlideshows);
  } else {
    // DOM 已经加载完成
    initializeSlideshows();
  }

  // 支持 Shopify 主题编辑器中的动态加载
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target;
      if (section.classList.contains('shopify-section--slideshow-swiper')) {
        // 等待一小段时间确保 DOM 更新完成
        setTimeout(() => {
          const swiperEl = section.querySelector('.swiper');
          if (swiperEl && !section.hasAttribute('data-swiper-initialized')) {
            initializeSlideshowSwiper(section, swiperEl);
          }
        }, 100);
      }
    });
  }
})();
